import { i18n, MessageDescriptor } from '@lingui/core';
import { throwErrorUnlessProduction } from '@luminovo/commons';
import * as React from 'react';

export type Translations = Record<string | number, MessageDescriptor>;

type Props = {
    /** A string or a number, since it is often an enum value. */
    text: string | number;
    translations: Translations;
};

/** Translates `text` with a `defineMessage` map of its finite values, falling back to `text` itself. */
export const TransEnum: React.FunctionComponent<Props> = ({ translations, text }) => {
    const message = translations[text];
    if (!message) {
        const knownTranslations = Object.keys(translations).join(', ');
        throwErrorUnlessProduction(
            new Error(
                [
                    `🚨 MISSING TRANSLATION: A translation is missing for text ${text}. `,
                    `There are already translations for ${knownTranslations}.\n`,
                    `🛠 How to fix: Add the missing translation.`,
                ].join(''),
            ),
            { fingerprint: [knownTranslations] },
        );
        return <>{text}</>;
    }
    return <>{i18n._(message)}</>;
};

/** `TransEnum` as a function: the translated `text`, or `text` itself when the map has no message. */
export const transEnum = (text: Props['text'], translations: Props['translations']): string => {
    const message = translations[text];
    if (!message) {
        const knownTranslations = Object.keys(translations).join(', ');
        throwErrorUnlessProduction(
            new Error(
                [
                    `🚨 MISSING TRANSLATION: A translation is missing for text ${JSON.stringify(text)}. `,
                    `There are already translations for ${knownTranslations}.\n`,
                    `🛠 How to fix: add the missing translation, then run "pnpm run intl" or `,
                    `follow the instructions on adding new translations in the README.md`,
                ].join(''),
            ),
            { fingerprint: [knownTranslations] },
        );
        return String(text);
    }
    return i18n._(message);
};
